import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "./useWorkspace";

export interface SearchResult {
  id: string;
  title: string;
  subtitle: string | null;
  type: "client" | "demand" | "content";
  route: string;
}

export function useGlobalSearch(query: string) {
  const { currentWorkspace } = useWorkspace();
  const trimmed = query.trim();
  const enabled = trimmed.length >= 2 && !!currentWorkspace?.id;

  const { data: clients = [] } = useQuery({
    queryKey: ["global-search-clients", currentWorkspace?.id, trimmed],
    queryFn: async () => {
      const { data } = await supabase
        .from("clients")
        .select("id, name, company")
        .eq("workspace_id", currentWorkspace!.id)
        .or(`name.ilike.%${trimmed}%,company.ilike.%${trimmed}%`)
        .limit(5);
      return data ?? [];
    },
    enabled,
  });

  const { data: demands = [] } = useQuery({
    queryKey: ["global-search-demands", currentWorkspace?.id, trimmed],
    queryFn: async () => {
      const { data } = await supabase
        .from("demands")
        .select("id, title, status")
        .eq("workspace_id", currentWorkspace!.id)
        .ilike("title", `%${trimmed}%`)
        .limit(5);
      return data ?? [];
    },
    enabled,
  });

  const { data: contents = [] } = useQuery({
    queryKey: ["global-search-contents", currentWorkspace?.id, trimmed],
    queryFn: async () => {
      const { data } = await supabase
        .from("contents")
        .select("id, title, content_type")
        .eq("workspace_id", currentWorkspace!.id)
        .ilike("title", `%${trimmed}%`)
        .limit(5);
      return data ?? [];
    },
    enabled,
  });

  const results = useMemo<SearchResult[]>(() => {
    if (!enabled) return [];
    return [
      ...clients.map((c) => ({
        id: c.id,
        title: c.name,
        subtitle: c.company,
        type: "client" as const,
        route: `/clientes/${c.id}`,
      })),
      ...demands.map((d) => ({
        id: d.id,
        title: d.title,
        subtitle: d.status,
        type: "demand" as const,
        route: "/demandas",
      })),
      ...contents.map((c) => ({
        id: c.id,
        title: c.title,
        subtitle: c.content_type,
        type: "content" as const,
        route: "/conteudo",
      })),
    ];
  }, [clients, demands, contents, enabled]);

  return { results, hasResults: results.length > 0, enabled };
}
